import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Menu, Slider, Text } from '@fragments';
import { IconArrowBackUp, IconCheck, IconChevronDown, IconItalic, IconLetterCaseUpper, IconPlus, IconTypography, IconUpload, IconUserCircle, IconX } from '@tabler/icons-react';
import { useArtEditorContext } from '../../../context';
import FontPicker from '../../FontPicker';
import NumberField from '../../NumberField';
import { Toggle } from '../../Inspector/controls';
import { avatarSource } from '../../Inspector';
import { applyBrandFonts } from '../../../../model/operations';
import { createImageLayer, TEXT_PRESETS } from '../../../../model/factory';
import { cssFamily, fontEntry, nearestWeight } from '../../../../model/fonts';
import { LIMITS } from '../../../../model/schema';
import { imageSource } from './PhotosPanel';

const ROLES = ['primary', 'secondary', 'accent', 'background', 'text'];
// The brand's text styles (2.10.0): the subheading falls back to the heading when unset.
const STYLES = ['heading', 'subheading', 'body'];
const WEIGHT_KEYS = { 100: 'weight100', 200: 'weight200', 300: 'weight300', 400: 'weight400', 500: 'weight500', 600: 'weight600', 700: 'weight700', 800: 'weight800', 900: 'weight900' };
const DEFAULT_FONTS = { heading: { family: 'Poppins', weight: 700 }, subheading: null, body: { family: 'Inter', weight: 400 } };
// Every part of a style; null = the template's (or, for the size, the preset's).
const fullStyle = font => (font ? { family: font.family, weight: font.weight, italic: font.italic ?? null, transform: font.transform ?? null,
  size: font.size ?? null, line_height: font.line_height ?? null, letter_spacing: font.letter_spacing ?? null } : null);
const sameKit = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const editable = kit => {
  const fonts = kit?.fonts || DEFAULT_FONTS;
  return { colors: kit?.colors || [], fonts: { heading: fullStyle(fonts.heading), subheading: fullStyle(fonts.subheading), body: fullStyle(fonts.body) },
    logo: kit?.logo || null, tone: kit?.tone || '' };
};

// One text style of the brand: a sample written in it (a tap opens its settings), then the
// face, weight, size of new texts, italic and uppercase, line height and letter spacing. A
// spacing left unset keeps each template's; the subheading follows the heading until set.
function TextStyle({ role, font, heading, open, onToggle, onChange, onReset, disabled }) {
  const { t } = useTranslation('artEditor');
  const own = font || heading;
  const entry = fontEntry(own.family);
  const weight = nearestWeight(entry.family, own.weight);
  const size = own.size ?? TEXT_PRESETS[role].font_size;
  const set = patch => onChange({ ...fullStyle(own), ...patch });
  // The sample is drawn through CSS variables: the app's global type rules win over plain inline styles.
  const sample = { '--art-font': cssFamily(entry.family), '--art-weight': weight, '--art-style': own.italic ? 'italic' : 'normal',
    '--art-case': own.transform === 'uppercase' ? 'uppercase' : 'none', '--art-tracking': `${own.letter_spacing || 0}em` };
  const details = [entry.family, t(WEIGHT_KEYS[weight]), own.size ? `${own.size}px` : null].filter(Boolean).join(' · ');
  return <div className={`art-brand-style ${open ? 'is-open' : ''}`}>
    <button type="button" className="art-brand-style__head" aria-expanded={open} onClick={onToggle}>
      <span className={`art-brand-style__sample is-${role}`} style={sample}>{t(`brandStyle_${role}`)}</span>
      <Text.caption contrast={6} className="art-brand-style__details">{font || role !== 'subheading' ? details : t('brandStyleSameAsHeading')}</Text.caption>
      <IconChevronDown className="art-brand-style__chevron" aria-hidden="true" />
    </button>
    {open && <div className="art-brand-style__body" inert={disabled ? '' : undefined}>
      <div className="art-brand-style__row"><Text.caption contrast={7}>{t('font')}</Text.caption>
        <FontPicker placement="right-start" value={entry.family} onPick={family => set({ family, weight: nearestWeight(family, weight), italic: fontEntry(family).italic ? own.italic : null })} /></div>
      <div className="art-brand-style__row"><Text.caption contrast={7}>{t('fontWeight')}</Text.caption>
        <Menu items={entry.weights.map(value => ({ label: t(WEIGHT_KEYS[value]), active: value === weight, onClick: () => set({ weight: value }) }))} placement="bottom-end">
          <Button size="small" variant="text" color="overlay" dropdown preventLoading aria-label={t('fontWeight')}>{t(WEIGHT_KEYS[weight])}</Button>
        </Menu></div>
      <div className="art-brand-style__row"><Text.caption contrast={7}>{t('brandStyleSize')}</Text.caption>
        <NumberField label={t('brandStyleSize')} value={size} min={LIMITS.fontSize[0]} max={LIMITS.fontSize[1]} onChange={value => set({ size: Math.round(value) })} /></div>
      <div className="art-brand-style__row"><Text.caption contrast={7}>{t('format')}</Text.caption>
        <span className="art-brand-style__toggles">
          <Toggle icon={IconItalic} label={t('italic')} active={own.italic === true} disabled={!entry.italic} onClick={() => set({ italic: own.italic === true ? null : true })} />
          <Toggle icon={IconLetterCaseUpper} label={t('uppercase')} active={own.transform === 'uppercase'} onClick={() => set({ transform: own.transform === 'uppercase' ? null : 'uppercase' })} />
        </span></div>
      <Spacing label={t('lineHeight')} value={own.line_height} fallback={TEXT_PRESETS[role].line_height} min={0.7} max={2.4} step={0.05} format={value => value.toFixed(2)}
        onChange={value => set({ line_height: value })} />
      <Spacing label={t('letterSpacing')} value={own.letter_spacing} fallback={0} min={-0.1} max={0.5} step={0.01} format={value => `${Math.round(value * 100)}`}
        onChange={value => set({ letter_spacing: value })} />
      <Text.caption contrast={5}>{t('brandStyleHint')}</Text.caption>
      {role === 'subheading' && font && <Button size="small" variant="text" color="overlay" preventLoading startIcon={<IconArrowBackUp />} onClick={onReset}>{t('brandStyleUseHeading')}</Button>}
    </div>}
  </div>;
}

// A spacing the kit may leave to each template (null): its slider shows the template's
// default until moved, and "Do modelo" clears it.
function Spacing({ label, value, fallback, min, max, step, format, onChange }) {
  const { t } = useTranslation('artEditor');
  return <div className="art-brand-style__spacing">
    <Slider label={label} min={min} max={max} step={step} value={value ?? fallback} formatLabel={number => (value === null ? t('brandStyleTemplate') : format(number))}
      onChange={(_, next) => onChange(next)} />
    {value !== null && <Button size="micro" variant="text" color="overlay" preventLoading icon={<IconArrowBackUp />} aria-label={t('brandStyleTemplate')} tooltip={t('brandStyleTemplate')}
      onClick={() => onChange(null)} />}
  </div>;
}

// The profile's brand kit: colors (a tap paints the selection, or the page when nothing
// is selected), the text styles, the logo, the Instagram picture (the arts' avatar) and the
// written tone the AI follows. Edits are a draft until "Save brand" sends them to the host.
export default function BrandKitPanel({ onClose }) {
  const { t } = useTranslation('artEditor');
  const { editor, brandKit, onSaveBrandKit, readOnly, uploadImages, paintSelection } = useArtEditorContext();
  const [draft, setDraft] = useState(() => editable(brandKit));
  const [saving, setSaving] = useState('idle');
  const [openStyle, setOpenStyle] = useState(null);
  const logoInput = useRef(null);
  const saved = editable(brandKit);
  useEffect(() => { setDraft(editable(brandKit)); }, [brandKit]);
  const dirty = !sameKit(draft, saved);
  const canSave = !!onSaveBrandKit;
  const doc = editor.document;
  const avatar = avatarSource(brandKit);

  const addColor = hex => setDraft(current => (current.colors.some(color => color.hex === hex) ? current
    : { ...current, colors: [...current.colors, { hex, name: null, role: ROLES.find(role => !current.colors.some(color => color.role === role)) || 'extra' }].slice(0, 12) }));
  const removeColor = hex => setDraft(current => ({ ...current, colors: current.colors.filter(color => color.hex !== hex) }));
  const setStyle = (role, font) => setDraft(current => ({ ...current, fonts: { ...current.fonts, [role]: font } }));
  const save = async () => {
    setSaving('saving');
    try { await onSaveBrandKit(draft); setSaving('saved'); } catch { setSaving('error'); }
  };
  const uploadLogo = async files => {
    const [image] = await uploadImages(files, { place: false });
    if (image) setDraft(current => ({ ...current, logo: { asset_id: image.id, url: image.url, width: image.width, height: image.height } }));
  };
  const addLogo = () => {
    if (!doc || !draft.logo) return;
    const source = imageSource({ id: draft.logo.asset_id, url: draft.logo.url, width: draft.logo.width, height: draft.logo.height });
    const layer = createImageLayer(doc, source, { fit: 'contain', binding: 'logo' });
    const width = Math.round(doc.width * 0.18);
    const height = Math.round(width / (source.width / source.height || 1));
    editor.add({ ...layer, width, height, x: doc.width - width - 64, y: doc.height - height - 64, name: t('logo') });
    onClose?.();
  };
  // The avatar goes in as a circle in the top left corner; its @ is the creator's to add.
  const addAvatar = () => {
    if (!doc || !avatar) return;
    const side = Math.round(doc.width * 0.12);
    const layer = createImageLayer(doc, avatar, { fit: 'cover', binding: 'avatar' });
    editor.add({ ...layer, width: side, height: side, corner_radius: Math.round(side / 2), x: 64, y: 64, name: t('binding_avatar') });
    onClose?.();
  };

  return <>
    {!brandKit?.configured && <div className="art-note"><Text.caption contrast={7}>{t('brandSuggested')}</Text.caption></div>}
    <section className="art-panel-section">
      <div className="art-panel-section__head"><Text.subheading contrast={6}>{t('brandColors')}</Text.subheading></div>
      <div className="art-brand-colors">
        {draft.colors.map(color => <div key={color.hex} className="art-brand-color">
          <button type="button" className="art-swatch is-large" style={{ '--swatch': color.hex }} disabled={readOnly} aria-label={`${t('applyColor')} ${color.name || color.hex}`}
            title={color.name || color.hex} onClick={() => paintSelection(color.hex)} />
          {canSave && <button type="button" className="art-brand-color__remove" aria-label={`${t('removeColor')} ${color.hex}`} onClick={() => removeColor(color.hex)}><IconX aria-hidden="true" /></button>}
        </div>)}
        {canSave && <label className="art-swatch is-large is-custom" title={t('addColor')}>
          <IconPlus aria-hidden="true" />
          <input type="color" aria-label={t('addColor')} onChange={event => addColor(event.target.value)} />
        </label>}
      </div>
      <Text.caption contrast={5}>{t('brandColorsHint')}</Text.caption>
    </section>
    <section className="art-panel-section">
      <div className="art-panel-section__head"><Text.subheading contrast={6}>{t('brandTextStyles')}</Text.subheading></div>
      <div className="art-brand-styles">
        {STYLES.map(role => <TextStyle key={role} role={role} font={draft.fonts[role]} heading={draft.fonts.heading} open={openStyle === role} disabled={!canSave}
          onToggle={() => setOpenStyle(current => (current === role ? null : role))} onChange={font => setStyle(role, font)} onReset={() => setStyle(role, null)} />)}
      </div>
      <Button size="small" variant="rounded" color="overlay" startIcon={<IconTypography />} disabled={readOnly || !doc} preventLoading
        onClick={() => editor.apply(current => applyBrandFonts(current, draft.fonts), { label: 'brandFonts' })}>{t('applyBrandFonts')}</Button>
    </section>
    <section className="art-panel-section">
      <div className="art-panel-section__head"><Text.subheading contrast={6}>{t('logo')}</Text.subheading></div>
      <input ref={logoInput} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={event => { const files = [...event.target.files]; event.target.value = ''; if (files.length) uploadLogo(files); }} />
      {draft.logo ? <div className="art-brand-logo">
        <img src={draft.logo.url} alt={t('logo')} />
        <div className="art-brand-logo__actions">
          <Button size="small" variant="rounded" color="inverse" preventLoading disabled={readOnly} onClick={addLogo}>{t('addLogo')}</Button>
          {canSave && <Button size="small" variant="text" color="overlay" preventLoading onClick={() => logoInput.current?.click()}>{t('replaceLogo')}</Button>}
        </div>
      </div> : canSave && <Button size="small" variant="dashed" color="overlay" startIcon={<IconUpload />} preventLoading onClick={() => logoInput.current?.click()}>{t('uploadLogo')}</Button>}
    </section>
    {avatar && <section className="art-panel-section">
      <div className="art-panel-section__head"><Text.subheading contrast={6}>{t('brandAvatar')}</Text.subheading></div>
      <div className="art-brand-logo is-avatar">
        <img src={avatar.url} alt={t('binding_avatar')} />
        <div className="art-brand-logo__actions">
          <Button size="small" variant="rounded" color="inverse" preventLoading disabled={readOnly || !doc} startIcon={<IconUserCircle />} onClick={addAvatar}>{t('addAvatar')}</Button>
        </div>
      </div>
      <Text.caption contrast={5}>{t('brandAvatarHint')}</Text.caption>
    </section>}
    <section className="art-panel-section">
      <div className="art-panel-section__head"><Text.subheading contrast={6}>{t('tone')}</Text.subheading></div>
      <textarea className="art-field" rows={3} value={draft.tone} disabled={!canSave} maxLength={600} placeholder={t('tonePlaceholder')} aria-label={t('tone')}
        onChange={event => setDraft(current => ({ ...current, tone: event.target.value }))} />
      <Text.caption contrast={5}>{t('toneHint')}</Text.caption>
    </section>
    {canSave && <div className="art-panel-footer">
      <Text.caption contrast={saving === 'error' ? 8 : 6} role="status" left={saving === 'saved' && !dirty ? <IconCheck /> : undefined}>
        {t(saving === 'saving' ? 'brandSaving' : saving === 'error' ? 'brandSaveFailed' : dirty ? 'brandUnsaved' : brandKit?.configured ? 'brandSaved' : 'brandNotSaved')}
      </Text.caption>
      <Button size="small" color="inverse" variant="rounded" disabled={(!dirty && brandKit?.configured) || saving === 'saving'} onClick={save}>{t('saveBrand')}</Button>
    </div>}
  </>;
}
